/**
 * The one strip of chrome the transport is allowed to write on.
 *
 * `docs/07` §3: **the socket is invisible when healthy.** What to say and when
 * to say nothing is `domain/connection-notice.ts`'s, tested against the copy
 * document itself; this file is the two timers that decide *when the answer
 * changes without any input changing* and the eight lines that draw it.
 *
 * Two timers, both from `docs/07`:
 *
 * - a reconnect says nothing for its first two seconds, because a blink is not
 *   an event and there are sixty boundaries an hour to blink at;
 * - `Reconnected.` retires itself two seconds after it appears.
 *
 * Neither polls. The component schedules exactly one timeout, at the instant the
 * verdict changes, and re-renders once.
 *
 * **It subscribes `runtime.state`, which is the panel-not-the-screen rule
 * (invariant 2) applied to a banner.** Every route mounts it beside its header
 * rather than reading the connection status in the route and passing it down —
 * a route that read it would reconcile its whole tree on every socket
 * transition, which at a `CONNECTING → OPEN → BACKOFF` sequence is three full
 * page renders for one line of text.
 */

import { useEffect, useState } from 'react'
import { useRuntimeState } from '@/app/use-runtime'
import { useWallet } from '@/components/wallet/WalletProvider'
import {
  connectionNotice,
  RECONNECTED_FOR_MS,
  RECONNECTING_AFTER_MS,
  type NoticeTone,
} from '@/domain/connection-notice'
import { cx } from '@/lib/cx'

/** `performance.now()`. Not `Date.now()` — invariant 7, and a duration measured
 *  against a wall clock that a laptop sleep can move is not a duration. */
function monoNow(): number {
  return globalThis.performance?.now?.() ?? 0
}

const TONE: Record<NoticeTone, string> = {
  info: 'bg-info-bg text-info-text',
  warning: 'bg-warning-bg text-warning-text',
  error: 'bg-danger-bg text-danger-text',
}

export interface ConnectionBannerProps {
  className?: string
}

export function ConnectionBanner({ className }: ConnectionBannerProps) {
  const state = useRuntimeState()
  const wallet = useWallet()

  // How long the connection has been up, or down — `BACKOFF` and `CONNECTING`
  // are one outage (`connectionClockAfter`). The instant comes off the runtime,
  // not out of a ref here: a duration measured inside a
  // component restarts when the component remounts, and this one is mounted per
  // route — a navigation mid-reconnect would restart the two-second clock and
  // the banner would never appear.
  const [, bump] = useState(0)
  const statusForMs = monoNow() - state.connectionSinceMs
  const notice = connectionNotice({
    status: state.connection,
    statusForMs,
    online: state.online,
    resumed: state.resumed,
    sessionExpired: state.sessionExpired,
    venueStatus: state.venueStatus,
    lastProblem: state.lastProblem,
  })

  // The one scheduled re-render: at two seconds down, and at two seconds up.
  // `state.connection` is the dependency, not `notice` — the object is new on
  // every render and would re-arm the timeout on every render with it.
  useEffect(() => {
    const elapsed = monoNow() - state.connectionSinceMs
    const boundary = state.connection === 'OPEN' ? RECONNECTED_FOR_MS : RECONNECTING_AFTER_MS
    if (elapsed >= boundary) return
    const handle = globalThis.setTimeout(() => bump((n) => n + 1), boundary - elapsed)
    return () => globalThis.clearTimeout(handle)
  }, [state.connection, state.connectionSinceMs])

  if (!notice) return null

  return (
    <div
      // `status`, not `alert`: this is advisory and continuous, and an assertive
      // live region would interrupt a screen-reader user mid-sentence sixty
      // times an hour.
      role="status"
      data-notice={notice.code}
      // **An overlay, never a row** (`<AppShell>`'s `notice`): it hangs over the
      // top of the page, so appearing and retiring move nothing under the
      // cursor. And it is `pointer-events-none` everywhere but the action: a
      // click aimed at whatever it happens to cover goes through to it, so the
      // banner cannot cause the misclick it was moved to stop.
      className={cx('pointer-events-none absolute inset-x-0 top-0 flex justify-center px-3', className)}
    >
      <div
        className={cx(
          'flex items-center gap-2 rounded-b-md px-3 py-1 text-caption font-medium shadow-popover',
          TONE[notice.tone],
        )}
      >
        <span>{notice.message}</span>
        {notice.action ? (
          <button
            type="button"
            className="pointer-events-auto underline underline-offset-2 hover:no-underline"
            onClick={wallet.signIn}
          >
            {notice.action.label}
          </button>
        ) : null}
      </div>
    </div>
  )
}
